import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { MarkEntityViewedBodyDto } from '@terra/shared-contracts-notification-center';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { NotificationCenterTarget } from '../../clients/tester-client/portal/portal-notification-center-api';
import { TARGET_TYPE } from '../../utils/target-scope.utils';

const HOME_NOTIFICATION_LIMIT = 20;

export const supportsNotificationCenter = (targetType: TargetTypeType) =>
  targetType === TARGET_TYPE.WEB_APPLICATION;

const isNotificationCenterTarget = ({
  targetType,
  targetId,
}: NotificationCenterTarget) =>
  targetId.length > 0 && supportsNotificationCenter(targetType);

const notificationCenterKeys = {
  actions: ({ targetType, targetId }: NotificationCenterTarget) =>
    ['notification-center', 'actions', targetType, targetId] as const,
  healthIssues: ({ targetType, targetId }: NotificationCenterTarget) =>
    ['notification-center', 'health-issues', targetType, targetId] as const,
};

const targetNotificationActionsQuery = (target: NotificationCenterTarget) =>
  queryOptions({
    queryKey: notificationCenterKeys.actions(target),
    queryFn: () =>
      terraProxyApiClient.getTargetNotificationActions({
        ...target,
        limit: HOME_NOTIFICATION_LIMIT,
      }),
    enabled: isNotificationCenterTarget(target),
    staleTime: 30_000,
  });

const targetHealthIssuesQuery = (target: NotificationCenterTarget) =>
  queryOptions({
    queryKey: notificationCenterKeys.healthIssues(target),
    queryFn: () =>
      terraProxyApiClient.getTargetHealthIssues({
        ...target,
        limit: HOME_NOTIFICATION_LIMIT,
      }),
    enabled: isNotificationCenterTarget(target),
    staleTime: 30_000,
  });

export function useTargetNotificationActions(target: NotificationCenterTarget) {
  return useQuery(targetNotificationActionsQuery(target));
}

export function useTargetHealthIssues(target: NotificationCenterTarget) {
  return useQuery(targetHealthIssuesQuery(target));
}

export function useDismissTargetNotification(target: NotificationCenterTarget) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ notificationId }: { notificationId: string }) =>
      terraProxyApiClient.dismissNotification({ ...target, notificationId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: notificationCenterKeys.actions(target),
      }),
  });
}

export function useMarkTargetEntityViewed(target: NotificationCenterTarget) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entity: MarkEntityViewedBodyDto) =>
      terraProxyApiClient.markEntityViewed({ ...target, entity }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: notificationCenterKeys.actions(target),
      }),
  });
}
